import React, { useState, useEffect, useMemo } from 'react';
import { IcTags, IcPackage, IcBoxes, IcSearch, IcPlus, IcMinus, IcClose, IcImage, IcTrash, IcPercent, IcBanknote, IcToggleOn, IcToggleOff, IcGift, IcCheck, IcGlobe, IcFlower, IcCalendar } from '../../theme/icons';
import Swal from '../../theme/swal';
import KI from '../../theme/palette';
import productService from '../../services/productService';
import promoService from '../../services/promoService';
import serviceManagementService from '../../services/serviceManagementService';
import { filterSearch } from '../../utils/search';

const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const fmt = (n) => `${round3(n).toFixed(3)} DT`;

// Type metadata used for cards, preview and the existing-promos list.
const TYPES = {
  ALL:       { label: 'Promotion sur tous les produits', short: 'Tous les produits, % de réduction', Icon: IcGlobe,  badge: 'bg-amber-50 text-amber-500' },
  SINGLE:    { label: 'Produit individuel',              short: 'Un % par produit choisi',          Icon: IcPackage,    badge: 'bg-red-50 text-primary' },
  PACK:      { label: 'Pack produit',                    short: 'Plusieurs produits, prix groupé',   Icon: IcBoxes,  badge: 'bg-amber-600 text-white' },
  PACK_SOIN: { label: 'Pack soin',                       short: 'Soins + produits ensemble',         Icon: IcFlower,    badge: 'bg-teal-50 text-teal-500' },
};

const AddPromo = () => {
  const [products, setProducts] = useState([]);
  const [soins, setSoins] = useState([]);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // shared form
  const [type, setType] = useState('ALL'); // 'ALL' | 'SINGLE' | 'PACK' | 'PACK_SOIN'
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [searchSoin, setSearchSoin] = useState('');

  // ALL
  const [allPct, setAllPct] = useState('');

  // SINGLE (produit individuel) — multiple products, a % each
  const [singleItems, setSingleItems] = useState([]); // [{...product, pct}]

  // PACK (pack produit)
  const [packItems, setPackItems] = useState([]); // [{...product, quantity}]
  const [packPrice, setPackPrice] = useState('');

  // PACK_SOIN
  const [psProducts, setPsProducts] = useState([]); // [{...product, quantity}]
  const [psSoins, setPsSoins] = useState([]);       // [{...service, quantity}]
  const [psMode, setPsMode] = useState('price');    // 'price' | 'percent'
  const [psPrice, setPsPrice] = useState('');
  const [psPct, setPsPct] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [pr, sv, pm] = await Promise.all([
          productService.getAllProducts(),
          serviceManagementService.getAllServices().catch(() => []),
          promoService.getPromos(),
        ]);
        setProducts(Array.isArray(pr) ? pr : (pr.results || []));
        const svList = Array.isArray(sv) ? sv : (sv.results || []);
        setSoins(svList.filter((s) => s.type === 'SOIN'));
        setPromos(pm);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const reloadPromos = async () => setPromos(await promoService.getPromos());

  const productResults = useMemo(() => {
    if (!search.trim()) return [];
    // Règle Ki Para (utils/search.js) : début de mot sur le nom, code /
    // code-barres « contient », « commence par » en tête ; jamais de rupture.
    const inStock = products.filter((p) => Number(p.stock || 0) > 0);
    return filterSearch(inStock, search, { text: (p) => p.name, codes: (p) => [p.code, p.barcode] }).slice(0, 8);
  }, [search, products]);

  const soinResults = useMemo(() => {
    if (!searchSoin.trim()) return [];
    return filterSearch(soins, searchSoin, { text: (s) => s.name }).slice(0, 8);
  }, [searchSoin, soins]);

  const onImage = (e) => {
    const f = e.target.files?.[0];
    setImageFile(f || null);
    setImagePreview(f ? URL.createObjectURL(f) : null);
  };

  // ----- SINGLE helpers (a % per product) -----
  const addSingle = (p) => {
    setSearch('');
    setSingleItems((prev) => prev.some((x) => x.id === p.id) ? prev : [...prev, { ...p, pct: '' }]);
  };
  const setSinglePct = (id, pct) => setSingleItems((prev) => prev.map((x) => x.id === id ? { ...x, pct } : x));
  const removeSingle = (id) => setSingleItems((prev) => prev.filter((x) => x.id !== id));
  const singlePromoPrice = (x) => round3((Number(x.price) || 0) * (1 - (parseFloat(x.pct) || 0) / 100));

  // ----- PACK helpers -----
  const addPack = (p) => {
    setSearch('');
    setPackItems((prev) => prev.some((x) => x.id === p.id)
      ? prev.map((x) => x.id === p.id ? { ...x, quantity: x.quantity + 1 } : x)
      : [...prev, { ...p, quantity: 1 }]);
  };
  const setPackQty = (id, q) => setPackItems((prev) => prev.map((x) => x.id === id ? { ...x, quantity: Math.max(1, q) } : x));
  const removePack = (id) => setPackItems((prev) => prev.filter((x) => x.id !== id));
  const packOriginal = round3(packItems.reduce((s, x) => s + (Number(x.price) || 0) * x.quantity, 0));
  const packPromo = round3(parseFloat(packPrice) || 0);

  // ----- PACK_SOIN helpers -----
  const addPsProduct = (p) => {
    setSearch('');
    setPsProducts((prev) => prev.some((x) => x.id === p.id)
      ? prev.map((x) => x.id === p.id ? { ...x, quantity: x.quantity + 1 } : x)
      : [...prev, { ...p, quantity: 1 }]);
  };
  const addPsSoin = (s) => {
    setSearchSoin('');
    setPsSoins((prev) => prev.some((x) => x.id === s.id)
      ? prev.map((x) => x.id === s.id ? { ...x, quantity: x.quantity + 1 } : x)
      : [...prev, { ...s, quantity: 1 }]);
  };
  const setPsQty = (kind, id, q) => {
    const setter = kind === 'soin' ? setPsSoins : setPsProducts;
    setter((prev) => prev.map((x) => x.id === id ? { ...x, quantity: Math.max(1, q) } : x));
  };
  const removePs = (kind, id) => {
    const setter = kind === 'soin' ? setPsSoins : setPsProducts;
    setter((prev) => prev.filter((x) => x.id !== id));
  };
  const psOriginal = round3(
    psProducts.reduce((s, x) => s + (Number(x.price) || 0) * x.quantity, 0) +
    psSoins.reduce((s, x) => s + (Number(x.price) || 0) * x.quantity, 0)
  );
  const psPromo = psMode === 'percent'
    ? round3(psOriginal * (1 - (parseFloat(psPct) || 0) / 100))
    : round3(parseFloat(psPrice) || 0);

  const resetForm = () => {
    setName(''); setDescription(''); setImageFile(null); setImagePreview(null);
    setStartDate(''); setEndDate(''); setSearch(''); setSearchSoin('');
    setAllPct('');
    setSingleItems([]);
    setPackItems([]); setPackPrice('');
    setPsProducts([]); setPsSoins([]); setPsMode('price'); setPsPrice(''); setPsPct('');
  };

  // Build the create payload (FormData when an image is attached) for one promo record.
  const buildPayload = ({ recName, recType, promo_price, percent, items, image }) => {
    if (image) {
      const fd = new FormData();
      fd.append('name', recName);
      fd.append('type', recType);
      fd.append('promo_price', promo_price);
      fd.append('percent', percent);
      fd.append('description', description || '');
      fd.append('is_active', 'true');
      fd.append('items', JSON.stringify(items));
      if (startDate) fd.append('start_date', startDate);
      if (endDate) fd.append('end_date', endDate);
      fd.append('image', image);
      return fd;
    }
    return {
      name: recName, type: recType, promo_price, percent,
      description: description || '', is_active: true, items,
      start_date: startDate || null, end_date: endDate || null,
    };
  };

  // « Enregistrer la promotion » reste INACTIF tant que les champs principaux ne sont
  // pas remplis (owner 2026-09-07). Même logique que les contrôles de save() ci-dessous,
  // évaluée au rendu : le bouton s'active dès que save() ne renverrait plus d'alerte.
  const pctOk = (v) => { const n = parseFloat(v) || 0; return n > 0 && n <= 100; };
  const canSave = Boolean(name.trim())
    && !(startDate && endDate && endDate < startDate)
    && (type === 'ALL' ? pctOk(allPct)
      : type === 'SINGLE' ? (singleItems.length > 0 && singleItems.every((x) => pctOk(x.pct)))
      : type === 'PACK' ? (packItems.length >= 2 && packPromo > 0)
      : (psSoins.length > 0 && psProducts.length > 0 && (psMode === 'percent' ? pctOk(psPct) : psPromo > 0)));

  const save = async () => {
    if (!name.trim()) return Swal.fire('Erreur', 'Donnez un nom à la promotion.', 'warning');
    if (startDate && endDate && endDate < startDate)
      return Swal.fire('Erreur', 'La date de fin doit être après la date de début.', 'warning');

    // Build the list of promo records to create (SINGLE produces one per product).
    let records = [];
    if (type === 'ALL') {
      const pct = parseFloat(allPct) || 0;
      if (pct <= 0 || pct > 100) return Swal.fire('Erreur', 'Entrez un pourcentage valide (1–100).', 'warning');
      records = [{ recName: name.trim(), recType: 'ALL', promo_price: 0, percent: pct, items: [] }];
    } else if (type === 'SINGLE') {
      if (singleItems.length === 0) return Swal.fire('Erreur', 'Choisissez au moins un produit.', 'warning');
      if (singleItems.some((x) => !(parseFloat(x.pct) > 0 && parseFloat(x.pct) <= 100)))
        return Swal.fire('Erreur', 'Entrez un pourcentage valide (1–100) pour chaque produit.', 'warning');
      records = singleItems.map((x) => ({
        recName: singleItems.length > 1 ? `${name.trim()} — ${x.name}` : name.trim(),
        recType: 'SINGLE',
        promo_price: singlePromoPrice(x),
        percent: parseFloat(x.pct) || 0,
        items: [{ product: x.id, quantity: 1 }],
      }));
    } else if (type === 'PACK') {
      if (packItems.length < 2) return Swal.fire('Erreur', 'Un pack doit contenir au moins 2 produits.', 'warning');
      if (packPromo <= 0) return Swal.fire('Erreur', 'Entrez un prix de pack valide.', 'warning');
      records = [{
        recName: name.trim(), recType: 'PACK', promo_price: packPromo, percent: 0,
        items: packItems.map((x) => ({ product: x.id, quantity: x.quantity })),
      }];
    } else { // PACK_SOIN
      if (psSoins.length === 0) return Swal.fire('Erreur', 'Ajoutez au moins un soin.', 'warning');
      if (psProducts.length === 0) return Swal.fire('Erreur', 'Ajoutez au moins un produit.', 'warning');
      if (psMode === 'percent') {
        const pct = parseFloat(psPct) || 0;
        if (pct <= 0 || pct > 100) return Swal.fire('Erreur', 'Entrez un pourcentage valide (1–100).', 'warning');
      } else if (psPromo <= 0) {
        return Swal.fire('Erreur', 'Entrez un prix de pack valide.', 'warning');
      }
      records = [{
        recName: name.trim(), recType: 'PACK_SOIN', promo_price: psPromo,
        percent: psMode === 'percent' ? (parseFloat(psPct) || 0) : 0,
        items: [
          ...psSoins.map((x) => ({ service: x.id, quantity: x.quantity, kind: 'service' })),
          ...psProducts.map((x) => ({ product: x.id, quantity: x.quantity })),
        ],
      }];
    }

    setSaving(true);
    try {
      for (const rec of records) {
        await promoService.createPromo(buildPayload({ ...rec, image: imageFile }));
      }
      await reloadPromos();
      resetForm();
      Swal.fire({
        icon: 'success',
        title: records.length > 1 ? `${records.length} promotions créées` : 'Promotion créée',
        timer: 1500, showConfirmButton: false,
      });
    } catch (e) {
      console.error(e);
      Swal.fire('Erreur', e?.detail || "Échec de l'enregistrement de la promotion.", 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (promo) => {
    try {
      await promoService.updatePromo(promo.id, { is_active: !promo.is_active });
      reloadPromos();
    } catch (e) { Swal.fire('Erreur', 'Impossible de modifier la promotion.', 'error'); }
  };
  const removePromo = async (promo) => {
    const r = await Swal.fire({ title: 'Supprimer cette promotion ?', text: promo.name, icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler', confirmButtonColor: KI.coralDark });
    if (!r.isConfirmed) return;
    try { await promoService.deletePromo(promo.id); reloadPromos(); } catch (e) { Swal.fire('Erreur', 'Échec de la suppression.', 'error'); }
  };

  // Un seul style de champ sur la page (owner 2026-09-09) : le kit .inp du site
  // (bordure #D1D5DB, 13.5px, focus = bordure + ring encre) à 44 px, rayon 6 —
  // comme les barres de « Nouvelle vente ». Plus de focus rouge.
  const inputCls = 'inp h-11 rounded-md w-full px-3.5';

  // Live-preview figures.
  const preview = (() => {
    if (type === 'ALL') return { original: 0, promo: 0, pct: parseFloat(allPct) || 0, label: 'Tous les produits' };
    if (type === 'SINGLE') return { count: singleItems.length, label: `${singleItems.length} produit(s) · un % chacun` };
    if (type === 'PACK') return { original: packOriginal, promo: packPromo, label: `Pack · ${packItems.length} produit(s)` };
    return { original: psOriginal, promo: psPromo, label: `${psSoins.length} soin(s) + ${psProducts.length} produit(s)` };
  })();

  // Reusable product search box. Called as a function (not <ProductSearch/>) so
  // the input keeps focus across keystrokes — a nested component would remount.
  const renderProductSearch = (onPick, label) => (
    <>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input className="inp h-11 rounded-md w-full pl-11 pr-3.5" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un produit (nom ou code)..." />
        {productResults.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {productResults.map((p) => (
              <button key={p.id} type="button" onClick={() => onPick(p)}
                className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center justify-between gap-2 border-b border-gray-50 last:border-0">
                <span className="min-w-0"><span className="text-sm font-medium text-gray-800 block truncate">{p.name}</span><span className="text-xs text-gray-400">{p.code || ''}</span></span>
                <span className="text-sm font-semibold text-gray-700 shrink-0">{fmt(p.price)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );

  // Même cadre que « Nouvelle vente » et les listes (owner 2026-09-07) : pleine
  // largeur, plus de plafond max-w — couleurs inchangées.
  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Owner 2026-09-09 : même ligne d'en-tête que VenteTabs sur « Nouvelle
          vente » (46px, mb-4) et chip .tab-chip-on (le bouton « Nouvelle vente »)
          à la place du chip gris « ajouter ». */}
      <div className="mb-4 flex items-center min-h-[46px]">
        <span className="tab-chip-on">
          <IcTags size={13} /> Ajouter une promotion
        </span>
      </div>

      {/* Owner 2026-09-09 : les deux cartes prennent la place exacte des deux
          panneaux de « Nouvelle vente » (.kp-split : 1fr + 360px, gap 16,
          .kp-pane collant, hauteur --kp-work-h). Le mt-[4.75rem] (76px) = le
          mb-4 de la ligne d'en-tête (16, fusionné par l'effondrement des marges)
          + la barre de recherche (48) + son mb-3 (12) qui n'existent pas ici →
          les cartes commencent à y=218 comme là-bas (192 sur mobile).
          Remplace l'ancien mt-tabs-gap (80px). */}
      <div className="kp-split mt-[4.75rem]">
        {/* ===== Form — panneau gauche, même enveloppe que la carte produits
            de « Nouvelle vente » (p-4, contenu défilant sans barre) ===== */}
        <div className="kp-pane bg-white border border-gray-200 rounded-2xl p-4">
          {/* Owner 2026-09-09 : la zone défilante (overflow) rognait le ring
              1px de focus des champs à gauche/droite → marge intérieure px-1
              compensée par -mx-1, le champ garde sa largeur visuelle. */}
          <div className="kp-scroll max-h-[60vh] lg:max-h-none px-1 -mx-1 pb-1">
          {/* Type cards */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {Object.entries(TYPES).map(([v, meta]) => {
              const Icon = meta.Icon;
              return (
                <button key={v} type="button" onClick={() => { setType(v); setSearch(''); setSearchSoin(''); }}
                  /* Carte sélectionnée en OR (owner 2026-09-08) : fond blanc,
                     filet 1.5px #F59E0B, titre et icône #D97706, sous-titre
                     #F59E0B. Les cartes non choisies gardent le gris. */
                  className={`flex items-center gap-3 p-4 rounded-2xl border-[1.5px] transition-all text-left ${type === v ? 'border-amber-500 bg-white' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${type === v ? 'bg-white text-amber-600' : 'bg-gray-100 text-gray-400'}`}><Icon /></span>
                  <div className="min-w-0">
                    <p className={`font-semibold truncate ${type === v ? 'text-amber-600' : 'text-gray-700'}`}>{meta.label}</p>
                    <p className={`text-xs ${type === v ? 'text-amber-500' : 'text-gray-400'}`}>{meta.short}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Name */}
          <label className="block text-sm font-medium text-gray-600 mb-1.5">Nom de la promotion</label>
          <input className={`${inputCls} mb-4`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Soldes d'été" />

          {/* ===== ALL ===== */}
          {type === 'ALL' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Pourcentage de réduction (sur tous les produits)</label>
              <div className="relative">
                <IcPercent className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                <input type="number" step="1" min="0" max="100" className={`${inputCls} pl-10`} value={allPct} onChange={(e) => setAllPct(e.target.value)} placeholder="Ex : 20" />
              </div>
              <p className="text-xs text-gray-400 mt-1.5">S'applique à tout le catalogue pendant la période choisie.</p>
            </div>
          )}

          {/* ===== SINGLE — produit individuel, % par produit ===== */}
          {type === 'SINGLE' && (
            <>
              {renderProductSearch(addSingle, 'Produits (un % par produit)')}
              <div className="space-y-2 mb-4">
                {singleItems.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-xl">Ajoutez un ou plusieurs produits.</p>
                ) : singleItems.map((x) => {
                  const promo = singlePromoPrice(x);
                  return (
                    <div key={x.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-2.5">
                      <span className="h-9 w-9 rounded-lg bg-gray-50 border flex items-center justify-center text-gray-300 shrink-0"><IcPackage /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{x.name}</p>
                        <p className="text-xs text-gray-400">Normal : {fmt(x.price)}{parseFloat(x.pct) > 0 && <> → <span className="text-primary font-semibold">{fmt(promo)}</span></>}</p>
                      </div>
                      <div className="relative w-24 shrink-0">
                        <input type="number" step="1" min="0" max="100" className="inp rounded-md w-full pl-3 pr-7 py-1.5 text-sm" value={x.pct} onChange={(e) => setSinglePct(x.id, e.target.value)} placeholder="%" />
                        <IcPercent className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300" size={11} />
                      </div>
                      <button onClick={() => removeSingle(x.id)} className="flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0" title="Retirer"><IcTrash /></button>
                    </div>
                  );
                })}
                {singleItems.length > 1 && <p className="text-xs text-gray-400">Une promotion distincte sera créée pour chaque produit.</p>}
              </div>
            </>
          )}

          {/* ===== PACK — pack produit ===== */}
          {type === 'PACK' && (
            <>
              {renderProductSearch(addPack, 'Ajouter des produits au pack')}
              <div className="space-y-2 mb-4">
                {packItems.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-xl">Ajoutez au moins 2 produits au pack.</p>
                ) : packItems.map((x) => (
                  <div key={x.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-2.5">
                    <span className="h-9 w-9 rounded-lg bg-gray-50 border flex items-center justify-center text-gray-300 shrink-0"><IcPackage /></span>
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-800 truncate">{x.name}</p><p className="text-xs text-gray-400">{fmt(x.price)} l'unité</p></div>
                    <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden shrink-0">
                      <button onClick={() => setPackQty(x.id, x.quantity - 1)} className="h-7 w-7 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-900"><IcMinus size={10} /></button>
                      <span className="w-7 text-center text-sm font-semibold tabular-nums">{x.quantity}</span>
                      <button onClick={() => setPackQty(x.id, x.quantity + 1)} className="h-7 w-7 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-900"><IcPlus size={10} /></button>
                    </div>
                    <button onClick={() => removePack(x.id)} className="flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0" title="Retirer"><IcTrash /></button>
                  </div>
                ))}
                {packItems.length > 0 && (
                  <div className="pt-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">Prix du pack (DT) · normal {fmt(packOriginal)}</label>
                    <input type="number" step="0.001" min="0" className={inputCls} value={packPrice} onChange={(e) => setPackPrice(e.target.value)} placeholder="Prix total du pack" />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== PACK_SOIN — pack soin ===== */}
          {type === 'PACK_SOIN' && (
            <>
              {/* Soin search */}
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Ajouter des soins</label>
              <div className="relative mb-3">
                <IcFlower className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input className="inp h-11 rounded-md w-full pl-11 pr-3.5" value={searchSoin} onChange={(e) => setSearchSoin(e.target.value)} placeholder="Rechercher un soin..." />
                {soinResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {soinResults.map((s) => (
                      <button key={s.id} type="button" onClick={() => addPsSoin(s)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between gap-2 border-b border-gray-50 last:border-0">
                        <span className="text-sm font-medium text-gray-800 truncate">{s.name}</span>
                        <span className="text-sm font-semibold text-gray-700 shrink-0">{fmt(s.price)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {psSoins.length > 0 && (
                <div className="space-y-2 mb-3">
                  {psSoins.map((x) => (
                    <div key={x.id} className="flex items-center gap-3 border border-teal-100 bg-teal-50/40 rounded-xl p-2.5">
                      <span className="h-9 w-9 rounded-lg bg-white border flex items-center justify-center text-teal-400 shrink-0"><IcFlower /></span>
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-800 truncate">{x.name}</p><p className="text-xs text-gray-400">{fmt(x.price)} l'unité</p></div>
                      <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden shrink-0">
                        <button onClick={() => setPsQty('soin', x.id, x.quantity - 1)} className="h-7 w-7 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-900"><IcMinus size={10} /></button>
                        <span className="w-7 text-center text-sm font-semibold tabular-nums">{x.quantity}</span>
                        <button onClick={() => setPsQty('soin', x.id, x.quantity + 1)} className="h-7 w-7 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-900"><IcPlus size={10} /></button>
                      </div>
                      <button onClick={() => removePs('soin', x.id)} className="flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0" title="Retirer"><IcTrash /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Product search */}
              {renderProductSearch(addPsProduct, 'Ajouter des produits')}
              {psProducts.length > 0 && (
                <div className="space-y-2 mb-3">
                  {psProducts.map((x) => (
                    <div key={x.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-2.5">
                      <span className="h-9 w-9 rounded-lg bg-gray-50 border flex items-center justify-center text-gray-300 shrink-0"><IcPackage /></span>
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-800 truncate">{x.name}</p><p className="text-xs text-gray-400">{fmt(x.price)} l'unité</p></div>
                      <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden shrink-0">
                        <button onClick={() => setPsQty('prod', x.id, x.quantity - 1)} className="h-7 w-7 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-900"><IcMinus size={10} /></button>
                        <span className="w-7 text-center text-sm font-semibold tabular-nums">{x.quantity}</span>
                        <button onClick={() => setPsQty('prod', x.id, x.quantity + 1)} className="h-7 w-7 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-900"><IcPlus size={10} /></button>
                      </div>
                      <button onClick={() => removePs('prod', x.id)} className="flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0" title="Retirer"><IcTrash /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pricing mode */}
              {(psSoins.length > 0 || psProducts.length > 0) && (
                <div className="mb-4">
                  <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 mb-3">
                    <button type="button" onClick={() => setPsMode('price')} className={`px-3 py-1.5 text-sm font-semibold flex items-center gap-1 ${psMode === 'price' ? 'bg-red-50 text-red-700 border border-red-300' : 'text-gray-600'}`}><IcBanknote size={12} /> Prix fixe</button>
                    <button type="button" onClick={() => setPsMode('percent')} className={`px-3 py-1.5 text-sm font-semibold flex items-center gap-1 ${psMode === 'percent' ? 'bg-red-50 text-red-700 border border-red-300' : 'text-gray-600'}`}><IcPercent size={12} /> Pourcentage</button>
                  </div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">{psMode === 'price' ? `Prix du pack (DT) · normal ${fmt(psOriginal)}` : 'Réduction (%) sur le total du pack'}</label>
                  {psMode === 'price' ? (
                    <input type="number" step="0.001" min="0" className={inputCls} value={psPrice} onChange={(e) => setPsPrice(e.target.value)} placeholder="Prix total du pack" />
                  ) : (
                    <input type="number" step="1" min="0" max="100" className={inputCls} value={psPct} onChange={(e) => setPsPct(e.target.value)} placeholder="Réduction (%)" />
                  )}
                </div>
              )}
            </>
          )}

          {/* Date limit (begin / end) — all types */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5 flex items-center gap-1.5"><IcCalendar className="text-gray-400" size={13} /> Date de début</label>
              <input type="date" className={inputCls} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5 flex items-center gap-1.5"><IcCalendar className="text-gray-400" size={13} /> Date de fin</label>
              <input type="date" className={inputCls} value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          {/* Image + description */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Image (optionnel)</label>
              <div className="flex items-center gap-3">
                <span className="h-16 w-16 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center text-gray-300">
                  {imagePreview ? <img src={imagePreview} alt="" className="h-full w-full object-cover" /> : <IcImage />}
                </span>
                <label className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 cursor-pointer hover:bg-gray-50">
                  Choisir<input type="file" accept="image/*" className="hidden" onChange={onImage} />
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Description (optionnel)</label>
              <textarea rows="2" className="inp rounded-md w-full px-3.5 py-2.5" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Détails de la promotion..." />
            </div>
          </div>

          </div>

          {/* Kit primaire vert (owner 2026-09-08) — l'icône ✓ suit le texte blanc
              du kit ; la mise en encre tentée juste avant a été abandonnée.
              Owner 2026-09-09 : bouton SORTI de la zone défilante → pied fixe
              du panneau, toujours visible pendant que le formulaire défile. */}
          <div className="shrink-0 pt-3 mt-1 border-t border-gray-100">
            <button onClick={save} disabled={saving || !canSave} className="btn-p w-full py-3"
              title={canSave ? undefined : 'Remplissez le nom et les champs principaux de la promotion'}>
              <IcCheck /> {saving ? 'Enregistrement…' : 'Enregistrer la promotion'}
            </button>
          </div>
        </div>

        {/* ===== Panneau droit — même carte que le panier de « Nouvelle vente »
            (.kp-card .kp-pane, blanc, bordure gray-200) : en-tête = Aperçu,
            corps défilant = Promotions existantes. Owner 2026-09-09 : les deux
            anciennes cartes (ambre + liste nue) fusionnées dans ce panneau. ===== */}
        <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2 mb-3 text-gray-700"><IcGift size={13} /> <span className="text-sm font-semibold uppercase tracking-wide">Aperçu</span></div>
            <p className="text-lg font-bold truncate text-gray-700">{name || 'Nom de la promotion'}</p>
            <p className="text-xs text-gray-500 mb-4">{TYPES[type].label} · {preview.label}</p>
            {/* Valeur de l'aperçu en #D97706 (owner 2026-09-08) — la couleur
                est portée par le conteneur pour couvrir les trois variantes :
                « −N% », le prix promo et « N produit(s) en promo ». */}
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-amber-600">
              {type === 'ALL' ? (
                <span className="text-3xl font-extrabold">−{preview.pct || 0}%</span>
              ) : type === 'SINGLE' ? (
                <span className="text-2xl font-extrabold">{preview.count || 0} produit(s) en promo</span>
              ) : (
                <>
                  <div className="flex items-end gap-3">
                    <span className="text-3xl font-extrabold">{fmt(preview.promo)}</span>
                    {preview.original > preview.promo && <span className="text-gray-400 line-through text-sm mb-1">{fmt(preview.original)}</span>}
                  </div>
                  {preview.original > preview.promo && (
                    <p className="bdg bdg-promo mt-2">
                      −{Math.round(((preview.original - preview.promo) / preview.original) * 100)}% · économie {fmt(preview.original - preview.promo)}
                    </p>
                  )}
                </>
              )}
            </div>
            {(startDate || endDate) && (
              <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5"><IcCalendar size={11} /> {startDate || '…'} → {endDate || '…'}</p>
            )}
          </div>

          {/* Existing promos — corps défilant du panneau (kp-scroll, comme la
              liste d'articles du panier). */}
          <div className="kp-scroll p-3">
            <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2"><IcTags className="text-gray-800" /> Promotions existantes ({promos.length})</h2>
            {loading ? (
              <p className="text-sm text-gray-400">Chargement…</p>
            ) : promos.length === 0 ? (
              <p className="text-sm text-gray-400">Aucune promotion pour l'instant.</p>
            ) : (
              <div className="space-y-2">
                {promos.map((pr) => {
                  const meta = TYPES[pr.type] || TYPES.SINGLE;
                  const Icon = meta.Icon;
                  return (
                    <div key={pr.id} className={`bg-white border-[1.5px] rounded-xl p-3 ${pr.is_active ? 'border-amber-500' : 'border-gray-100 opacity-60'}`}>
                      <div className="flex items-start gap-2">
                        <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${meta.badge}`}><Icon /></span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-800 truncate">{pr.name}</p>
                          <p className="text-xs text-gray-400">{pr.type_display}{pr.type !== 'ALL' && ` · ${pr.items?.length || 0} article(s)`}</p>
                          <p className="text-sm mt-0.5">
                            {pr.type === 'ALL'
                              ? <span className="font-bold text-primary">−{Math.round(pr.percent)}%</span>
                              : <><span className="font-bold text-amber-600">{fmt(pr.promo_price)}</span> {pr.original_total > pr.promo_price && <span className="text-gray-400 line-through text-xs ml-1">{fmt(pr.original_total)}</span>}</>}
                          </p>
                          {(pr.start_date || pr.end_date) && <p className="text-[11px] text-gray-400 mt-0.5">{pr.start_date || '…'} → {pr.end_date || '…'}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <button onClick={() => toggleActive(pr)} title={pr.is_active ? 'Désactiver' : 'Activer'} className={pr.is_active ? 'text-emerald-500' : 'text-gray-300'}>{pr.is_active ? <IcToggleOn size={22} /> : <IcToggleOff size={22} />}</button>
                          <button onClick={() => removePromo(pr)} className="text-red-600 hover:bg-red-50"><IcTrash size={13} /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPromo;
